import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating views
import { getCities } from 'models/cities'; // Importing function to retrieve city data
import { getTags } from 'models/tags'; // Importing function to retrieve tag data
import { getPositions } from 'models/positions'; // Importing function to retrieve position data
import 'webix/photo'; // Importing Webix photo module
import 'webix/tinymce/tinymce'; // Importing TinyMCE editor from Webix
import { devConfig, profile } from '../../config/config'; // Importing development configuration and profile settings
import { players } from '../../models/players-model'; // Importing players model

/**
 * Class representing a view for adding information to the bet shop.
 * @extends JetView
 */
export default class addInformationBetShopView extends JetView {
    /**
     * Configures the view layout.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        // Retrieve the localization service from the app
        const _ = this.app.getService('locale')._;
        // Get the current screen size configuration
        const screen = this.app.config.size;
        // Define available languages for selection
        const lenguages = [
            { id: 'ES', value: 'español (o castellano) (ES)' },
            { id: 'EN', value: 'English (EN)' },
            { id: 'PT', value: 'Portugués (PT)' },
        ];
        // Main configuration object for the UI component
        const main = {
            padding: 15, // Padding around the main component
            margin: 15, // Margin around the main component
            minWidth: 250, // Minimum width of the main component
            cols: [
                {
                    rows: [
                        {
                            view: 'text', // Text input for login
                            name: 'LoginAccess', // Name of the input field
                            label: _('Login para acceso'), // Label for the input field
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Login para acceso'), // Placeholder text
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'text', // Text input for password
                            name: 'Password', // Name of the input field
                            id: 'Password', // ID of the input field
                            hidden: profile.PartnerSelected == 8, // Hide if PartnerSelected is 8
                            label: _('Clave'), // Label for the input field
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Clave'), // Placeholder text
                            required: true, // Indicates that this field is required
                            invalidMessage: _(
                                'Mínimo 6 caracteres y un número', // Validation message for password
                            ),
                            // Custom validation function for password
                            validate: function (password) {
                                return (
                                    password.length >= 6 && // Minimum length check
                                    /[0-9]/.test(password) && // Check for at least one digit
                                    !/(.)\1\1+/g.test(password) // Check for three consecutive identical characters
                                );
                            },
                            // Event handler for onBlur event
                            on: {
                                onBlur: function () {
                                    $$('Password').validate(); // Validate password on blur
                                },
                            },
                        },
                        {
                            view: 'richselect', // Dropdown for state selection
                            name: 'State', // Name of the input field
                            localId: 'State:combo', // Local ID for the component
                            label: "<span class='icon-state'></span>" + _('Estado'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                { id: 'A', value: _('Activo') }, // Active option
                                { id: 'I', value: _('Inactivo') }, // Inactive option
                                { id: 'R', value: _('Retirado') }, // Retired option
                            ],
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for preferred language selection
                            name: 'PreferredLanguage', // Name of the input field
                            localId: 'PreferredLanguage:combo', // Local ID for the component
                            label: _('Idioma preferido:'), // Label for the input field
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: lenguages, // Options populated from the languages array
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'text', // Text input for user name
                            name: 'Name', // Name of the input field
                            label: "<span class='icon-user'></span>" + _('Nombre del usuario'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Nombre del usuario'), // Placeholder text
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for country selection
                            name: 'CountryId', // Name of the input field
                            id: 'country', // ID of the input field
                            localId: 'Country:combo', // Local ID for the component
                            label: "<span class='icon-language'></span>" + _('País'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [], // Options to be populated dynamically
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for currency selection
                            name: 'CurrencyId', // Name of the input field
                            id: 'currency', // ID of the input field
                            localId: 'Currency:combo', // Local ID for the component
                            label: "<span class='webix_icon mdi mdi-coin'></span>" + _('Moneda'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [], // Options to be populated dynamically
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for partner selection
                            name: 'Partner', // Name of the input field
                            label: _('Partner'), // Label for the input field
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: profile.PartnerSelected, // Default value from profile
                            options: profile.Partners, // Options populated from profile
                            hidden: !profile.Partners.length > 2, // Hide if less than 3 partners
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for establishment type selection
                            name: 'TypeEstablishment', // Name of the input field
                            id: 'TypeEstablishment', // ID of the input field
                            localId: 'Country:combo', // Local ID for the component
                            label: "<span class='icon-language'></span>" + _('Tipo de establecimiento'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [], // Options to be populated dynamically
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'richselect', // Dropdown for commission payment selection
                            name: 'ComissionsPayment', // Name of the input field
                            hidden: devConfig.getPartnerSelected() != '8', // Hide if partner selected is not 8
                            localId: 'State:combo', // Local ID for the component
                            label: "<span class='icon-state'></span>" + _('Pago de comisiones'), // Label with an icon
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                { id: 'S', value: _('Saldo') }, // Balance option
                                { id: 'F', value: _('Factura') }, // Invoice option
                            ],
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                    ],
                },
            ],
        };
        /**
         * Main configuration options for the UI component.
         * @type {Object}
         * @property {number} padding - Padding around the component.
         * @property {number} margin - Margin around the component.
         * @property {number} minWidth - Minimum width of the component.
         * @property {Array} cols - Array of column configurations.
         */
        const options_main = {
            padding: 15,
            margin: 15,
            minWidth: 250,
            cols: [
                {
                    rows: [
                        {
                            hidden: true, // Indicates that this row is hidden by default.
                            rows: [
                                { view: 'label', label: _('Estado especial') }, // Label for special status.
                                {
                                    id: 'SpecialStatus', // Unique identifier for the switch.
                                    value: 1, // Default value for the switch (active).
                                    view: 'switch', // Switch UI component.
                                    onLabel: _('Activo'), // Label when switch is on.
                                    offLabel: _('Inactivo'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Bloqueado para ventas'), // Label for sales lock status.
                                },
                                {
                                    id: 'Lockedsales', // Unique identifier for the sales lock switch.
                                    view: 'switch', // Switch UI component.
                                    onLabel: _('Si'), // Label when switch is on.
                                    offLabel: _('No'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Imprime recibo caja'), // Label for receipt printing option.
                                },
                                {
                                    id: 'PrintReceiptBox', // Unique identifier for the receipt printing switch.
                                    view: 'switch', // Switch UI component.
                                    value: 1, // Default value for the switch (enabled).
                                    onLabel: _('Si'), // Label when switch is on.
                                    offLabel: _('No'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            rows: [
                                { view: 'label', label: _('Permite recargas') }, // Label for recharge permission.
                                {
                                    id: 'AllowsRecharges', // Unique identifier for the recharge permission switch.
                                    view: 'switch', // Switch UI component.
                                    value: 1, // Default value for the switch (enabled).
                                    onLabel: _('Si'), // Label when switch is on.
                                    offLabel: _('No'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Permite activar reg'), // Label for registration activation permission.
                                },
                                {
                                    id: 'ActivateRegistration', // Unique identifier for the registration activation switch.
                                    view: 'switch', // Switch UI component.
                                    value: 1, // Default value for the switch (enabled).
                                    onLabel: _('Si'), // Label when switch is on.
                                    offLabel: _('No'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Programa de identificación de IP Activo'), // Label for IP identification status.
                                },
                                {
                                    id: 'IPIdentification', // Unique identifier for the IP identification switch.
                                    view: 'switch', // Switch UI component.
                                    value: 0, // Default value for the switch (disabled).
                                    onLabel: _('Si'), // Label when switch is on.
                                    offLabel: _('No'), // Label when switch is off.
                                },
                            ],
                        },
                        {
                            view: 'text', // Text input for latitude.
                            name: 'Latitud', // Name of the input field.
                            type: 'number', // Input type is number.
                            label: _('Latitud'), // Label for the input field.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Latitud'), // Placeholder text for the input field.
                            invalidMessage: _('Campo Requerido'), // Message shown when the field is invalid.
                        },
                        {
                            view: 'text', // Text input for longitude.
                            name: 'Longitud', // Name of the input field.
                            type: 'number', // Input type is number.
                            label: _('Longitud'), // Label for the input field.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Longitud'), // Placeholder text for the input field.
                            invalidMessage: _('Campo Requerido'), // Message shown when the field is invalid.
                        },
                        {
                            view: 'text', // Text input for IP address.
                            name: 'IP', // Name of the input field.
                            label: _('IP'), // Label for the input field.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('IP'), // Placeholder text for the input field.
                            invalidMessage: _('Campo Requerido'), // Message shown when the field is invalid.
                        },
                        {
                            view: 'textarea', // Textarea for the header of the prize payment receipt.
                            name: 'HeaderPrintPrizePaymentReceipt', // Name of the input field.
                            label: _('Parte Superior Impresión recibo de pago de premio'), // Label for the textarea.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Parte Superior Impresión recibo de pago de premio'), // Placeholder text for the textarea.
                            disabled: false, // Indicates that the textarea is enabled.
                            height: 100, // Height of the textarea.
                        },
                        {
                            view: 'textarea', // Textarea for the footer of the prize payment receipt.
                            name: 'FooterPrintPrizePaymentReceipt', // Name of the input field.
                            label: _('Parte Inferior Impresión recibo de pago de premio'), // Label for the textarea.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Parte Inferior Impresión recibo de pago de premio'), // Placeholder text for the textarea.
                            disabled: false, // Indicates that the textarea is enabled.
                            height: 100, // Height of the textarea.
                        },
                        {
                            view: 'textarea', // Textarea for the header of the retirement payment receipt.
                            name: 'HeaderPrintRetirementPaymentReceipt', // Name of the input field.
                            label: _('Parte Superior Impresión recibo de pago de retiro'), // Label for the textarea.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Parte Superior Impresión recibo de pago de retiro'), // Placeholder text for the textarea.
                            disabled: false, // Indicates that the textarea is enabled.
                            height: 100, // Height of the textarea.
                        },
                        {
                            view: 'textarea', // Textarea for the footer of the retirement payment receipt.
                            name: 'FooterPrintRetirementPaymentReceipt', // Name of the input field.
                            label: _('Parte Inferior Impresión recibo de pago de retiro'), // Label for the textarea.
                            labelPosition: 'top', // Position of the label.
                            placeholder: _('Parte Inferior Impresión recibo de pago de retiro'), // Placeholder text for the textarea.
                            disabled: false, // Indicates that the textarea is enabled.
                            height: 100, // Height of the textarea.
                        },
                    ],
                },
            ],
        };
        /**
         * Configuration object for betshop details form.
         * @type {Object}
         * @property {number} margin - The margin around the form.
         * @property {string} id - The unique identifier for the form.
         * @property {number} padding - The padding inside the form.
         * @property {number} minWidth - The minimum width of the form.
         * @property {Array} cols - Array of column configurations for the form.
         */
        const betshop_details = {
            margin: 5,
            id: 'betshop_details',
            padding: 15,
            minWidth: 250,
            cols: [
                {
                    rows: [
                        /**
                         * Contact Name input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'ContactName',
                            label: _('Nombre del contacto'), // Label for the input field
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Nombre del contacto'), // Placeholder text
                            required: true, // Indicates that this field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        /**
                         * Document number input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Document',
                            type: 'number', // Specifies the input type as number
                            label: _('Número cédula'),
                            labelPosition: 'top',
                            placeholder: _('Numero cédula'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Email input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Email',
                            label: _('Correo electrónico'),
                            labelPosition: 'top',
                            placeholder: _('Correo electrónico'),
                            required: true,
                            invalidMessage: _('Campo inválido'),
                        },
                        /**
                         * Description input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Description',
                            label: _("<span class='icon-file-text1'></span> Descripción"),
                            labelPosition: 'top',
                            placeholder: _('Descripción'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Manager Phone input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'ManagerPhone',
                            type: 'number',
                            label: _('Teléfono'),
                            labelPosition: 'top',
                            placeholder: _('Teléfono'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Concessionaire selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'Concessionaire',
                            id: 'Concessionaire',
                            localId: 'Concessionaire:combo',
                            label: _('Concesionario'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [], // Options for the select field
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Subconcessionaire selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'Subconcessionaire',
                            id: 'Subconcessionaire',
                            localId: 'Subconcessionaire:combo',
                            label: _('Subconcesionario'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [],
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Second Subconcessionaire selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'Subconcessionaire2',
                            id: 'Subconcessionaire2',
                            localId: 'Subconcessionaire2:combo',
                            label: _('Subconcesionario2'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [],
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Region selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'RegionId',
                            id: 'ProvinceRegion',
                            localId: 'ProvinceRegion:combo',
                            label: _('Provincia/Región'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [],
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * City selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'CityId',
                            id: 'City',
                            localId: 'City:combo',
                            label: "<span class='icon-language'></span>" + _('Ciudad'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [],
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Zone selection field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'richselect',
                            name: 'Zone',
                            id: 'Zone',
                            label: "<span class='icon-language'></span>" + _('Zona'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [],
                            required: false,
                        },
                        /**
                         * Address input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Address',
                            label: _('Dirección'),
                            labelPosition: 'top',
                            placeholder: _('Dirección'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * District input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'District',
                            label: _('Barrio'),
                            labelPosition: 'top',
                            placeholder: _('Barrio'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Mobile Phone input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'MobilePhone',
                            type: 'number',
                            label: _('Teléfono Mobile'),
                            labelPosition: 'top',
                            placeholder: _('Teléfono Mobile'),
                            required: true,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Facebook input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Facebook',
                            hidden: devConfig.getPartnerSelected() != '8', // Conditional visibility based on partner selection
                            label: _('Facebook'),
                            labelPosition: 'top',
                            placeholder: _('Facebook'),
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Facebook verification switch configuration.
                         * @type {Object}
                         */
                        {
                            hidden: devConfig.getPartnerSelected() != '8',
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Facebook Verificado'),
                                },
                                {
                                    id: 'FacebookVerification',
                                    view: 'switch',
                                    onLabel: _('Si'), // Label for the 'on' state
                                    offLabel: _('No'), // Label for the 'off' state
                                },
                            ],
                        },
                        /**
                         * Instagram input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'Instagram',
                            hidden: devConfig.getPartnerSelected() != '8',
                            label: _('Instagram'),
                            labelPosition: 'top',
                            placeholder: _('Instagram'),
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Instagram verification switch configuration.
                         * @type {Object}
                         */
                        {
                            hidden: devConfig.getPartnerSelected() != '8',
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Instagram Verificado'),
                                },
                                {
                                    id: 'InstagramVerification',
                                    view: 'switch',
                                    onLabel: _('Si'),
                                    offLabel: _('No'),
                                },
                            ],
                        },
                        /**
                         * WhatsApp input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'WhatsApp',
                            hidden: devConfig.getPartnerSelected() != '8',
                            label: _('WhatsApp'),
                            labelPosition: 'top',
                            placeholder: _('WhatsApp'),
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * WhatsApp verification switch configuration.
                         * @type {Object}
                         */
                        {
                            hidden: devConfig.getPartnerSelected() != '8',
                            rows: [
                                {
                                    view: 'label',
                                    label: _('WhatsApp Verificado'),
                                },
                                {
                                    id: 'WhatsAppVerification',
                                    view: 'switch',
                                    onLabel: _('Si'),
                                    offLabel: _('No'),
                                },
                            ],
                        },
                        /**
                         * Other Social Media input field configuration.
                         * @type {Object}
                         */
                        {
                            view: 'text',
                            name: 'OtherSocialMedia',
                            hidden: devConfig.getPartnerSelected() != '8',
                            label: _('Otras Redes Sociales'),
                            labelPosition: 'top',
                            placeholder: _('Otras Redes Sociales'),
                            required: false,
                            invalidMessage: _('Campo Requerido'),
                        },
                        /**
                         * Other Social Media verification switch configuration.
                         * @type {Object}
                         */
                        {
                            hidden: devConfig.getPartnerSelected() != '8',
                            rows: [
                                {
                                    view: 'label',
                                    label: _('Otras Redes Sociales Verificado'),
                                },
                                {
                                    id: 'OtherSocialMediaVerification',
                                    view: 'switch',
                                    onLabel: _('Si'),
                                    offLabel: _('No'),
                                },
                            ],
                        },
                    ],
                },
            ],
        };
        // Restricts a numeric text field to digits only (blocks '-', '.', letters, etc.)
        const digitsOnlyHandlers = {
            onBeforePaste: function (text) {
                return String(text).replace(/\D+/g, '');
            },
            onKeyPress: function (code) {
                return (
                    (code >= 48 && code <= 57) || // digits
                    code === 8 || // backspace
                    code === 9 || // tab
                    code === 13 || // enter
                    code === 46 || // delete
                    code === 37 || // arrow left
                    code === 39 // arrow right
                );
            },
            onTimedKeyPress: function () {
                var clean = String(this.getValue() || '').replace(/\D+/g, '');
                if (this.getValue() !== clean) this.setValue(clean);
            },
        };
        /**
         * Configuration object for the center-right section of the UI.
         * Contains layout properties and form fields for user input.
         */
        const center_right = {
            padding: 15, // Padding around the section
            margin: 15, // Margin around the section
            cols: [
                // Array of column configurations
                {
                    rows: [
                        // Array of row configurations
                        {
                            view: 'text', // Type of input field
                            name: 'DailyQuotaReloads', // Unique name for the input
                            type: 'number', // Input type
                            label: _('Limite diario de recargas'), // Label for the input
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Limite diario de recargas'), // Placeholder text
                            required: true, // Indicates if the field is required
                            invalidMessage: _('Campo Requerido'), // Message shown when validation fails
                        },
                        {
                            view: 'text',
                            name: 'MinimumRechargeAmount',
                            id: 'MinimumRechargeAmount',
                            type: 'number',
                            label: _('Monto mínimo de recarga'),
                            labelPosition: 'top',
                            placeholder: _('Monto mínimo de recarga'),
                            required: false, // Optional field
                            invalidMessage: _('Ingresa un monto de recarga válido. Solo se permiten valores numéricos positivos.'),
                            on: digitsOnlyHandlers,
                        },
                        {
                            view: 'text',
                            name: 'MaximumRechargeAmount',
                            id: 'MaximumRechargeAmount',
                            type: 'number',
                            label: _('Monto máximo de recarga'),
                            labelPosition: 'top',
                            placeholder: _('Monto máximo de recarga'),
                            required: false, // Optional field
                            on: digitsOnlyHandlers,
                            invalidMessage: _('Ingresa un monto de recarga válido. Solo se permiten valores numéricos positivos.'),
                        },
                        {
                            view: 'text',
                            name: 'MinimumBet',
                            type: 'number',
                            label: _('Apuesta mínima'),
                            labelPosition: 'top',
                            placeholder: _('Apuesta mínima'),
                            required: false, // Not required
                            hidden: true, // Field is hidden by default
                            invalidMessage: _('Campo Requerido'),
                        },
                        {
                            view: 'text',
                            name: 'DailyLimit',
                            type: 'number',
                            label: _('Límite diario'),
                            labelPosition: 'top',
                            placeholder: _('Límite diario'),
                            required: false, // Not required
                            hidden: true, // Field is hidden by default
                            invalidMessage: _('Campo Requerido'),
                        },
                        {
                            view: 'text',
                            name: 'MaximumWithdrawalAmount',
                            type: 'number',
                            label: _('Máximo monto por retiro'),
                            labelPosition: 'top',
                            placeholder: _('Máximo monto por retiro'),
                            required: true, // Indicates if the field is required
                            invalidMessage: 'Campo Requerido', // Message shown when validation fails
                        },
                        {
                            view: 'text',
                            name: 'MaximumPrizePaymentAmount',
                            type: 'number',
                            label: _('Máximo monto pago premio'),
                            labelPosition: 'top',
                            placeholder: _('Máximo monto pago premio'),
                            required: true, // Indicates if the field is required
                            invalidMessage: 'Campo Requerido', // Message shown when validation fails
                        },
                        {
                            view: 'text',
                            name: 'PrizePaymentTax',
                            type: 'number',
                            label: _('Impuesto al pago de premio (GGR)'),
                            labelPosition: 'top',
                            placeholder: _('Impuesto al pago de premio (GGR)'),
                            required: true, // Indicates if the field is required
                            invalidMessage: 'Campo Requerido', // Message shown when validation fails
                        },
                        {
                            rows: [
                                // Nested rows for a switch input
                                { view: 'label', label: _('Es Propio?') }, // Label for the switch
                                {
                                    id: 'BetShopOwn', // Unique ID for the switch
                                    view: 'switch', // Type of input
                                    onLabel: _('Si'), // Label when switch is on
                                    offLabel: _('No'), // Label when switch is off
                                },
                            ],
                        },
                        {
                            rows: [
                                // Nested rows for a switch input
                                { view: 'label', label: _('Tipo de Punto') }, // Label for the switch
                                {
                                    id: 'BetShopType', // Unique ID for the switch
                                    value: 1, // Default value for the switch
                                    view: 'switch', // Type of input
                                    onLabel: _('Físico'), // Label when switch is on
                                    offLabel: _('Virtual'), // Label when switch is off
                                },
                            ],
                        },
                        {
                            rows: [
                                // Nested rows for a switch input
                                {
                                    view: 'label',
                                    label: _('Entrega Premio Físico?'), // Label for the switch
                                },
                                {
                                    id: 'PhysicalPrize', // Unique ID for the switch
                                    view: 'switch', // Type of input
                                    onLabel: _('Si'), // Label when switch is on
                                    offLabel: _('No'), // Label when switch is off
                                },
                            ],
                        },
                    ],
                },
            ],
        };
        // Define the right section layout with margin and rows
        const right = {
            margin: 5, // Margin around the right section
            rows: [], // Array to hold rows in the right section
        };

        // Define the upper section layout with an ID and rows
        const upper_section = {
            id: 'b5', // Unique identifier for the upper section
            rows: [
                // Array of rows in the upper section
                {
                    collapsed: false, // Indicates if the section is collapsed
                    multi: true, // Allows multiple sections to be expanded
                    view: 'accordion', // Defines the view type as accordion
                    css: 'betShop', // CSS class for styling
                    margin: 15, // Margin around the upper section
                    responsive: 'b5', // Responsive design identifier
                    cols: [
                        // Array of columns within the row
                        {
                            multi: true, // Allows multiple sections to be expanded
                            view: 'accordion', // Defines the view type as accordion
                            css: 'betShop', // CSS class for styling
                            collapsed: false, // Indicates if the column is collapsed
                            paddingY: 10, // Vertical padding for the column
                            type: 'clean', // Type of the column layout
                            rows: [
                                // Array of rows within the column
                                {
                                    header: _('Datos Principales'), // Header text for the row
                                    body: {
                                        rows: [main], // Body content of the row, referencing 'main'
                                    },
                                },
                            ],
                        },
                        {
                            multi: true, // Allows multiple sections to be expanded
                            view: 'accordion', // Defines the view type as accordion
                            css: 'betShop', // CSS class for styling
                            collapsed: false, // Indicates if the column is collapsed
                            paddingY: 10, // Vertical padding for the column
                            type: 'clean', // Type of the column layout
                            rows: [
                                // Array of rows within the column
                                {
                                    header: _('Datos Configuración'), // Header text for the row
                                    body: {
                                        rows: [options_main], // Body content of the row, referencing 'options_main'
                                    },
                                },
                            ],
                        },
                        {
                            multi: true, // Allows multiple sections to be expanded
                            view: 'accordion', // Defines the view type as accordion
                            css: 'betShop', // CSS class for styling
                            collapsed: false, // Indicates if the column is collapsed
                            paddingY: 10, // Vertical padding for the column
                            type: 'clean', // Type of the column layout
                            rows: [
                                // Array of rows within the column
                                {
                                    header: _('Datos Punto de venta'), // Header text for the row
                                    body: {
                                        rows: [betshop_details], // Body content of the row, referencing 'betshop_details'
                                    },
                                },
                            ],
                        },
                        {
                            multi: true, // Allows multiple sections to be expanded
                            view: 'accordion', // Defines the view type as accordion
                            css: 'betShop', // CSS class for styling
                            collapsed: false, // Indicates if the column is collapsed
                            paddingY: 10, // Vertical padding for the column
                            type: 'clean', // Type of the column layout
                            rows: [
                                // Array of rows within the column
                                {
                                    header: _('Configuración Punto de Venta'), // Header text for the row
                                    body: {
                                        rows: [center_right], // Body content of the row, referencing 'center_right'
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        };
        const buttons = {
            /** Margin between buttons */
            margin: 10,
            /** Array of button configurations */
            cols: [
                {},
                {
                    /** Button to clear the form */
                    view: 'button',
                    value: _('Limpiar'), // Button label
                    autowidth: true, // Automatically adjust width
                    click: () => {
                        /** Clears the form when clicked */
                        this.getRoot().clear();
                    },
                },
                {
                    /** Button to save the form data */
                    view: 'button',
                    value: _('Guardar'), // Button label
                    type: 'form', // Indicates this button submits a form
                    autowidth: true, // Automatically adjust width
                    click: () => {
                        /** Validates the form and saves data if valid */
                        if (this.getRoot().validate()) {
                            const newdata = this.getRoot().getValues(); // Get form values
                            /** Validate recharge amount fields (optional, zero or positive numbers, min <= max).
                             * The backend stores an unset amount as 0 (e.g. when the field was left empty on a
                             * previous save), so 0 must be accepted as a valid value here too — otherwise simply
                             * reopening and resaving a record that already has 0 would fail validation. */
                            const isValidRechargeAmount = (val) => val === '' || (/^\d+(\.\d+)?$/.test(val) && parseFloat(val) >= 0);
                            const minRecharge = $$('MinimumRechargeAmount').getValue();
                            const maxRecharge = $$('MaximumRechargeAmount').getValue();
                            // Full text for the modal/toast (plenty of room to wrap there).
                            const invalidAmountMessage = _(
                                'Ingresa un monto de recarga válido. Solo se permiten valores numéricos positivos.',
                            );
                            const invalidRangeMessage = _(
                                'El monto mínimo de recarga no puede ser mayor al monto máximo de recarga.',
                            );
                            // Short text for the inline field label — the "Configuración Punto de
                            // Venta" column is too narrow to wrap the full message without clipping.
                            const invalidAmountFieldMessage = _('Monto inválido. Solo positivos.');
                            const invalidRangeFieldMessage = _('El mínimo no puede ser mayor al máximo.');
                            let rechargeAmountsValid = true;
                            if (!isValidRechargeAmount(minRecharge)) {
                                this.getRoot().markInvalid('MinimumRechargeAmount', invalidAmountFieldMessage);
                                rechargeAmountsValid = false;
                            } else {
                                // markInvalid(name, false) clears just this field;
                                // clearValidation() takes no argument and would reset the whole form.
                                this.getRoot().markInvalid('MinimumRechargeAmount', false);
                            }
                            if (!isValidRechargeAmount(maxRecharge)) {
                                this.getRoot().markInvalid('MaximumRechargeAmount', invalidAmountFieldMessage);
                                rechargeAmountsValid = false;
                            } else {
                                this.getRoot().markInvalid('MaximumRechargeAmount', false);
                            }
                            if (
                                rechargeAmountsValid &&
                                minRecharge !== '' &&
                                maxRecharge !== '' &&
                                parseFloat(minRecharge) > parseFloat(maxRecharge)
                            ) {
                                this.getRoot().markInvalid('MinimumRechargeAmount', invalidRangeFieldMessage);
                                this.getRoot().markInvalid('MaximumRechargeAmount', invalidRangeFieldMessage);
                                rechargeAmountsValid = false;
                            }
                            if (!rechargeAmountsValid) {
                                webix.message({
                                    title: _('¡Error!'),
                                    type: 'alert-error',
                                    text: isValidRechargeAmount(minRecharge) && isValidRechargeAmount(maxRecharge)
                                        ? invalidRangeMessage
                                        : invalidAmountMessage,
                                });
                                return;
                            }
                            /** Set SpecialStatus based on checkbox value */
                            if ($$('SpecialStatus').getValue() == 1) {
                                newdata.SpecialStatus = 'A'; // Active
                            } else {
                                newdata.SpecialStatus = 'I'; // Inactive
                            }
                            /** Set Lockedsales based on checkbox value */
                            if ($$('Lockedsales').getValue() == 1) {
                                newdata.Lockedsales = 'S'; // Yes
                            } else {
                                newdata.Lockedsales = 'N'; // No
                            }
                            /** Set PrintReceiptBox based on checkbox value */
                            if ($$('PrintReceiptBox').getValue() == 1) {
                                newdata.PrintReceiptBox = 'S'; // Yes
                            } else {
                                newdata.PrintReceiptBox = 'N'; // No
                            }
                            /** Set AllowsRecharges based on checkbox value */
                            if ($$('AllowsRecharges').getValue() == 1) {
                                newdata.AllowsRecharges = 'S'; // Yes
                            } else {
                                newdata.AllowsRecharges = 'N'; // No
                            }
                            /** Set ActivateRegistration based on checkbox value */
                            if ($$('ActivateRegistration').getValue() == 1) {
                                newdata.ActivateRegistration = 'S'; // Yes
                            } else {
                                newdata.ActivateRegistration = 'N'; // No
                            }
                            /** Set IPIdentification based on checkbox value */
                            if ($$('IPIdentification').getValue() == 1) {
                                newdata.IPIdentification = 'S'; // Yes
                            } else {
                                newdata.IPIdentification = 'N'; // No
                            }
                            /** Set BetShopOwn based on checkbox value */
                            if ($$('BetShopOwn').getValue() == 1) {
                                newdata.BetShopOwn = 'S'; // Yes
                            } else {
                                newdata.BetShopOwn = 'N'; // No
                            }
                            /** Set BetShopType based on checkbox value */
                            if ($$('BetShopType').getValue() == 1) {
                                newdata.BetShopType = 'F'; // Type F
                            } else {
                                newdata.BetShopType = 'V'; // Type V
                            }
                            /** Set PhysicalPrize based on checkbox value */
                            if ($$('PhysicalPrize').getValue() == 1) {
                                newdata.PhysicalPrize = 'S'; // Yes
                            } else {
                                newdata.PhysicalPrize = 'N'; // No
                            }
                            /** Additional checks for partner with ID '8' */
                            if (devConfig.getPartnerSelected() == '8') {
                                /** Set FacebookVerification based on checkbox value */
                                if ($$('FacebookVerification').getValue() == 1) {
                                    newdata.FacebookVerification = 'S'; // Yes
                                } else {
                                    newdata.FacebookVerification = 'N'; // No
                                }
                                /** Set InstagramVerification based on checkbox value */
                                if ($$('InstagramVerification').getValue() == 1) {
                                    newdata.InstagramVerification = 'S'; // Yes
                                } else {
                                    newdata.InstagramVerification = 'N'; // No
                                }
                                /** Set WhatsAppVerification based on checkbox value */
                                if ($$('WhatsAppVerification').getValue() == 1) {
                                    newdata.WhatsAppVerification = 'S'; // Yes
                                } else {
                                    newdata.WhatsAppVerification = 'N'; // No
                                }
                                /** Set OtherSocialMediaVerification based on checkbox value */
                                if ($$('OtherSocialMediaVerification').getValue() == 1) {
                                    newdata.OtherSocialMediaVerification = 'S'; // Yes
                                } else {
                                    newdata.OtherSocialMediaVerification = 'N'; // No
                                }
                            }
                            /** Trigger save event with new data */
                            this.app.callEvent('betshop:save', [newdata]);
                        } else {
                            /** Show error message if validation fails */
                            webix.message({
                                title: _('¡Error!'), // Error title
                                type: 'alert-error', // Error type
                                text: _('Completa los campos...'), // Error message
                            });
                        }
                    },
                },
            ],
        };
        // Set the UI to full screen mode
        webix.ui.fullScreen();

        // Return a configuration object for a Webix form
        return {
            // Define the view type as 'form'
            view: 'form',

            // Unique identifier for the form
            id: 'formId',

            // Enable vertical scrolling
            scroll: 'y',

            // Apply custom CSS class to the form
            css: 'out',

            // Define the layout of the form with rows containing buttons and an upper section
            rows: [buttons, upper_section],

            // Define validation rules for the form fields
            rules: {
                // Email field must follow email format validation
                Email: webix.rules.isEmail,
            },

            // Configuration for elements within the form
            elementsConfig: {
                // Event handlers for form elements
                on: {
                    // Trigger validation on change of any form element
                    onChange: function (newv, oldv) {
                        this.validate();
                    },
                },
            },
        };
    }

    /**
     * Initializes the view and its components.
     * @param {Object} form - The form object to initialize.
     */
    init(form) {
        // Retrieve the localization service and assign it to the variable '_'
        const _ = this.app.getService('locale')._;
        // Preserve the context of 'this' for later use
        var vthis = this;
        // Flags to track concessionaire and subconcessionaire states
        var isConce = false;
        var isSubConce = false;
        // Show the 'addBetShop' UI component
        $$('addBetShop').show();
        // Make an AJAX request to get agent countries
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Agent/GetAgentCountries', '', {
                // Handle error response
                error: function (text, data, XmlHttpRequest) {
                    webix.message(_('Error! '));
                },
                // Handle successful response
                success: function (text, data, XmlHttpRequest) {
                    // Parse the response and normalize property names
                    var data = JSON.parse(text.replace(/"Id"/g, '"id"').replace(/"Name"/g, '"value"'));
                    // Clear and populate the country list with the received data
                    var optionsProvinceRegions = $$('country').getList();
                    optionsProvinceRegions.clearAll();
                    optionsProvinceRegions.parse(data.Data);
                },
            });
        // Make an AJAX request to get agents with two levels
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'Agent/GetAgentsTwoLevels2', '', {
                // Handle error response
                error: function (text, data, XmlHttpRequest) {
                    webix.message(_('Error! '));
                },
                // Handle successful response
                success: function (text, data, XmlHttpRequest) {
                    // Parse the response and normalize property names
                    var data = JSON.parse(text.replace(/"Id"/g, '"id"').replace(/"Name"/g, '"value"'));
                    // Check if there are no children in the data
                    if (data.Data.Children == null) {
                        // Hide related UI components if no children
                        $$('Concessionaire').hide();
                        $$('Subconcessionaire').hide();
                        $$('Subconcessionaire2').hide();
                    } else {
                        // Clear and populate the concessionaire list with the received data
                        var options = $$('Concessionaire').getList();
                        options.clearAll();
                        options.parse(data.Data.Children);
                        // If only one child exists, set its value and disable the field
                        if (data.Data.Children.length == 1) {
                            if (data.Data.Children[0].id == profile.AgentId) {
                                isConce = true;
                            }
                            $$('Concessionaire').setValue(data.Data.Children[0].id);
                            $$('Concessionaire').define('disabled', true);
                        } else {
                            // Reset the field if multiple children exist
                            $$('Concessionaire').setValue('');
                            $$('Concessionaire').define('disabled', false);
                        }
                    }
                },
            });
        // Make an AJAX request to get types of establishments
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'BetShop/GetTypeEstablishment', '', {
                // Handle error response
                error: function (text, data, XmlHttpRequest) {
                    webix.message(_('Error! '));
                },
                // Handle successful response
                success: function (text, data, XmlHttpRequest) {
                    // Parse the response
                    var data = JSON.parse(text);
                    // Clear and populate the type establishment list with the received data
                    var optionsTypeEstablishment = $$('TypeEstablishment').getList();
                    optionsTypeEstablishment.clearAll();
                    optionsTypeEstablishment.parse(data.Data);
                },
            });
        // Make an AJAX request to get zones
        webix
            .ajax()
            .headers({
                'Content-type': 'application/json',
            })
            .post(devConfig.geturlApi() + 'BetShop/GetZones', '', {
                // Handle error response
                error: function (text, data, XmlHttpRequest) {
                    webix.message(_('Error! '));
                },
                // Handle successful response
                success: function (text, data, XmlHttpRequest) {
                    // Parse the response
                    var data = JSON.parse(text);
                    // Clear and populate the zones list with the received data
                    var optionsZones = $$('Zone').getList();
                    optionsZones.clearAll();
                    optionsZones.parse(data.Data);
                },
            });
        // Trigger an event to update the form with user parameters
        this.app.callEvent('form:update', [this.getParam('user', true)]);
        // Event listener for customer data update
        this.on(this.app, 'customer:updatedata', (person) => form.setValues(person));
        // Event listener for person selection
        this.on(this.app, 'person:select', (person) => form.setValues(person));
        // Event listener for saving the bet shop
        this.on(this.app, 'betshop:save', (data) => {
            // Make an AJAX request to save the bet shop
            webix
                .ajax()
                .headers({
                    'Content-type': 'application/json',
                })
                .post(devConfig.geturlApi() + 'BetShop/SaveBetShop', data, {
                    // Handle error response
                    error: function (text, data, XmlHttpRequest) {
                        webix.message(_('Error'));
                    },
                    // Handle successful response
                    success: function (text, data, XmlHttpRequest) {
                        // Parse the response
                        var json = JSON.parse(text);
                        // Check for errors in the response
                        if (json.HasError) {
                  
                            const rechargeErrorMessages = {
                                300544: _('Monto inválido. Solo positivos.'),
                                300545: _('El mínimo no puede ser mayor al máximo.'),
                                300546: _('El máximo no puede ser menor al mínimo.'),
                            };
                            if (rechargeErrorMessages[json.CodeError]) {
                                form.markInvalid('MinimumRechargeAmount', rechargeErrorMessages[json.CodeError]);
                                form.markInvalid('MaximumRechargeAmount', rechargeErrorMessages[json.CodeError]);
                            } else {
                                webix.message({
                                    title: '¡Error!',
                                    type: 'alert-error',
                                    text: _('ERROR' + json.CodeError.toString()),
                                });
                            }
                        } else {
                            // Show success message
                            webix.message(_('Guardado'));
                            // Redirect if an ID is returned
                            if (json.id != undefined) {
                                vthis.show(
                                    'betShopManagement?user=' + json.id + '/betShopManagement.informationBetShop',
                                );
                                $$('addBetShop').hide();
                            } else {
                                webix.message(_('Error'));
                            }
                        }
                    },
                });
        });
        // Event listener for country selection change
        $$('country').attachEvent('onChange', function (newv, oldv) {
            // Get the selected country and update related fields
            var optionscountries = this.getList().getItem(newv);
            var optionscurrencies = $$('currency').getList();
            optionscurrencies.clearAll();
            optionscurrencies.parse(optionscountries.currencies);
            var optionsProvinceRegions = $$('ProvinceRegion').getList();
            optionsProvinceRegions.clearAll();
            optionsProvinceRegions.parse(optionscountries.departments);
        });
        // Event listener for province region selection change
        $$('ProvinceRegion').attachEvent('onChange', function (newv, oldv) {
            // Get the selected province region and update related fields
            var optionsProvinceRegions = this.getList().getItem(newv);
            var optionsCity = $$('City').getList();
            optionsCity.clearAll();
            optionsCity.parse(optionsProvinceRegions.cities);
        });
        // Event listener for concessionaire selection change
        $$('Concessionaire').attachEvent('onChange', function (newv, oldv) {
            // Get the selected concessionaire and update related fields
            var optionsProvinceRegions = this.getList().getItem(newv);
            var optionsCity = $$('Subconcessionaire').getList();
            optionsCity.clearAll();
            if (optionsProvinceRegions != undefined) {
                optionsCity.parse(optionsProvinceRegions.Children);
                // Handle single child case
                if (optionsProvinceRegions.Children.length == 1) {
                    if (!isConce) {
                        if (optionsProvinceRegions.Children[0].id == profile.AgentId) {
                            isSubConce = true;
                        }
                        $$('Subconcessionaire').setValue(optionsProvinceRegions.Children[0].id);
                        $$('Subconcessionaire').define('disabled', true);
                    } else {
                        // Reset the field if already a concessionaire
                        $$('Subconcessionaire').setValue('');
                        $$('Subconcessionaire').define('disabled', false);
                    }
                } else {
                    // Reset the field if multiple children exist
                    $$('Subconcessionaire').setValue('');
                    $$('Subconcessionaire').define('disabled', false);
                }
            }
        });
        // Event listener for subconcessionaire selection change
        $$('Subconcessionaire').attachEvent('onChange', function (newv, oldv) {
            // Get the selected subconcessionaire and update related fields
            var optionsProvinceRegions = this.getList().getItem(newv);
            var optionsCity2 = $$('Subconcessionaire2').getList();
            optionsCity2.clearAll();
            if (optionsProvinceRegions != undefined) {
                optionsCity2.parse(optionsProvinceRegions.Children);
                // Handle single child case
                if (optionsProvinceRegions.Children.length == 1) {
                    if (!isSubConce && !isConce) {
                        $$('Subconcessionaire2').setValue(optionsProvinceRegions.Children[0].id);
                        $$('Subconcessionaire2').define('disabled', true);
                    } else {
                        // Reset the field if already a subconcessionaire
                        $$('Subconcessionaire2').setValue('');
                        $$('Subconcessionaire2').define('disabled', false);
                    }
                } else {
                    // Reset the field if multiple children exist
                    $$('Subconcessionaire2').setValue('');
                    $$('Subconcessionaire2').define('disabled', false);
                }
            }
        });
    }

    /**
     * Retrieves and localizes combo options for positions, tags, and cities.
     * Updates the corresponding combo lists with localized values.
     */
    getLocalizedComboOptions() {
        const _ = this.app.getService('locale')._; // Localization function
        let p_options = webix.copy(getPositions()); // Copying position options
        p_options.map((x) => (x.value = _(x.value))); // Localizing position values
        this.$$('position:combo').getPopup().getList().parse(p_options); // Parsing position options into the combo

        let t_options = webix.copy(getTags()); // Copying tag options
        t_options.map((x) => (x.value = _(x.value))); // Localizing tag values
        this.$$('tags:combo').getPopup().getList().parse(t_options); // Parsing tag options into the combo

        let c_options = webix.copy(getCities()); // Copying city options
        c_options.map((x) => (x.value = _(x.value))); // Localizing city values
        this.$$('cities:combo').getPopup().getList().parse(c_options); // Parsing city options into the combo
    }
}

// FILE DOCUMENTED
